/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Authentication provider abstraction
 *
 * Defines the provider-neutral authentication contract used by the webapp.
 * Auth0 and Keycloak adapters implement this contract so application code does
 * not depend on a concrete identity-provider SDK.
 */

import {InjectionToken} from '@angular/core';
import {Observable} from 'rxjs';

/**
 * Provider-neutral error code signalling that no usable session exists.
 *
 * Adapters set this code when a silent token retrieval fails because the user
 * must authenticate interactively again (missing, expired, or revoked session).
 * Application-level handling reacts by starting a new login instead of showing
 * an error page.
 */
export const LOGIN_REQUIRED_ERROR_CODE = 'login_required';

/**
 * Minimal OpenID Connect user data required by the webapp.
 *
 * The identity-provider adapters map SDK-specific user payloads to this type.
 */
export interface IAuthenticatedUser {
  /** Stable OpenID Connect subject identifier for the authenticated user. */
  readonly sub: string;

  /** Optional display name supplied by the identity provider. */
  readonly name?: string;

  /** Optional email address supplied by the identity provider. */
  readonly email?: string;
}

/**
 * Provider-neutral authentication error shown by application-level UI.
 *
 * Adapters must not expose raw SDK error objects outside the authentication
 * boundary because their fields differ between identity providers.
 */
export interface IAuthenticationError {
  /** Human-readable description of the authentication failure. */
  readonly message: string;

  /** Optional provider-neutral error code for UI-specific handling. */
  readonly code?: string;
}

/**
 * Intent supplied when beginning an interactive authentication flow.
 */
export interface IAuthenticationLoginOptions {
  /** Requests the provider's registration flow when that capability is enabled. */
  readonly registration?: boolean;

  /** Optional application-relative route to restore after authentication. */
  readonly returnUrl?: string;
}

/**
 * Options supplied when ending the current authenticated session.
 */
export interface IAuthenticationLogoutOptions {
  /** Optional application-relative route to open after the logout completes. */
  readonly returnUrl?: string;
}

/**
 * Provider-neutral identity-provider contract for the webapp.
 *
 * Implementations return a valid access token from `getAccessToken()`. They
 * must refresh an expiring token before resolving when their SDK supports
 * refresh-token-based renewal.
 */
export interface IAuthenticationProvider {
  /** Emits whether the browser currently has an authenticated session. */
  readonly isAuthenticated$: Observable<boolean>;

  /** Emits the authenticated OIDC user, or `null` when no session exists. */
  readonly user$: Observable<IAuthenticatedUser | null>;

  /** Emits the latest normalized authentication error, or `null` when absent. */
  readonly error$: Observable<IAuthenticationError | null>;

  /**
   * Gets an access token suitable for an authenticated backend request.
   *
   * @param forceRefresh - Whether the adapter must bypass its access-token cache.
   * @returns A promise resolving to a valid access token.
   */
  getAccessToken(forceRefresh?: boolean): Promise<string>;

  /**
   * Starts the identity provider's interactive authentication flow.
   *
   * @param options - Login intent and optional post-login route.
   * @returns A promise that resolves after the provider accepts the redirect.
   */
  login(options?: IAuthenticationLoginOptions): Promise<void>;

  /**
   * Ends the identity provider session and redirects the browser if required.
   *
   * @param options - Optional post-logout route.
   * @returns A promise that resolves after the provider accepts the logout.
   */
  logout(options?: IAuthenticationLogoutOptions): Promise<void>;
}

/**
 * Injects the active build-time-selected authentication provider.
 *
 * The token has no default implementation: `AppModule` provides the adapter
 * selected by `BP_AUTH_PROVIDER` during application composition, and specs
 * register a mock (see `testing/authentication-provider.mock.ts`).
 */
export const AUTHENTICATION_PROVIDER =
  new InjectionToken<IAuthenticationProvider>('AuthenticationProvider');
